import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../AuthContext";
import { LoadingContext } from "../LoadingContext";
import Swal from "sweetalert2";
// Reuse the Product Management styling so both pages look identical, and the Add New
// Product styling so this form is visually identical to that one.
import "./AdminProductList.css";
import "./AdminAddProduct.css";
import "./AdminPromotion.css";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function AdminPromotion() {
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  const { authFetch } = useContext(AuthContext);
  const { setIsLoading } = useContext(LoadingContext);
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    setIsLoading(true);
    setLoading(true);
    try {
      const res = await authFetch(`${API_BASE_URL}/api/Promotions/all`);
      setPromotions(res.ok ? await res.json() : []);
    } catch (e) {
      Swal.fire("Error", "Failed to load promotions.", "error");
    } finally {
      setIsLoading(false);
      setLoading(false);
    }
  };


  const handleDelete = async (id, itemName) => {
    const result = await Swal.fire({
      title: "Remove Promotion?",
      text: `Remove "${itemName}" from promotions?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Remove",
    });
    if (!result.isConfirmed) return;

    try {
      const res = await authFetch(`${API_BASE_URL}/api/Promotions/${id}`, { method: "DELETE" });
      if (res.ok) {
        fetchData();
        Swal.fire({ icon: "success", title: "Removed", timer: 1500, showConfirmButton: false });
      } else {
        Swal.fire("Error", "Failed to remove promotion.", "error");
      }
    } catch (e) {
      Swal.fire("Error", "Failed to remove promotion.", "error");
    }
  };

  const filtered = promotions.filter(
    (p) =>
      p.productName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.productCode?.toLowerCase().includes(searchTerm.toLowerCase())
  );


  return (
    <div className="admin-product-list-container admin-promo-page">
      {/* Header */}
      <div className="admin-product-list-header">
        <button
          className="admin-product-list-back-btn"
          onClick={() => navigate("/admin/dashboard")}
        >
          <i className="fas fa-arrow-left"></i> Back to Dashboard
        </button>
      </div>

      <div className="admin-product-list-title">
        <h1>Promotion Products</h1>
      </div>

      {/* Actions */}
      <div className="admin-product-list-actions">
        <div className="admin-product-list-actions-right">
          <button className="admin-product-list-btn-add" onClick={() => navigate('/admin/promotions/add')}>
            <i className="fas fa-plus"></i> Add Promotion Product
          </button>
        </div>
      </div>

      {/* Filter Section */}
      <div className="admin-product-list-filter">
        <div className="admin-product-list-filter-group">
          <label>Search</label>
          <input
            type="text"
            placeholder="Filter by product name or code"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="admin-product-list-filter-input"
          />
        </div>
      </div>

      {/* Promotion Cards */}
      <div className="admin-product-list-grid">
        {loading ? (
          <div className="admin-product-list-no-results">
            <i className="fas fa-spinner fa-spin"></i>
            <p className="primary-message">Loading promotions...</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="admin-product-list-no-results">
            <i className="fas fa-tags"></i>
            <p className="primary-message">
              No promotion products found. Click "Add Promotion Product" to create one.
            </p>
          </div>
        ) : (
          filtered.map((item) => {
            const discount =
              item.originalPrice && item.originalPrice > 0
                ? Math.min(99, Math.round((1 - item.promotionPrice / item.originalPrice) * 100))
                : null;

            return (
              <div key={item.id} className="admin-product-list-card">
                <div className="admin-product-list-image-container">
                  {item.imageUrl ? (
                    <img
                      src={item.imageUrl}
                      alt={item.productName}
                      className="admin-product-list-image"
                    />
                  ) : (
                    <div className="admin-promo-no-img"><i className="fas fa-image"></i></div>
                  )}
                </div>

                <div className="admin-product-list-details">
                  <h2 className="admin-product-list-name">{item.productName}</h2>

                  {item.variantLabel && (
                    <div style={{ fontSize: "13px", color: "#0288d1", marginBottom: "6px" }}>
                      <i className="fas fa-tag" style={{ marginRight: "5px" }}></i>
                      {item.variantLabel}
                    </div>
                  )}

                  <div className="admin-product-list-price">
                    {item.originalPrice != null && (
                      <span className="admin-promo-original-price">
                        RM {parseFloat(item.originalPrice).toFixed(2)}
                      </span>
                    )}
                    <span className="admin-promo-price">
                      RM {parseFloat(item.promotionPrice).toFixed(2)}
                    </span>
                    {discount != null && discount > 0 && (
                      <span className="admin-promo-discount-badge">{discount}% OFF</span>
                    )}
                  </div>

                  <div className="admin-product-list-info">
                    <div className="admin-product-list-detail">
                      <span className="admin-product-list-detail-label">Product Code:</span>
                      <span className="admin-product-list-detail-value">
                        {item.productCode || "-"}
                      </span>
                    </div>
                    <div className="admin-product-list-detail">
                      <span className="admin-product-list-detail-label">UOM:</span>
                      <span className="admin-product-list-detail-value">{item.uom || "-"}</span>
                    </div>
                    <div className="admin-product-list-detail">
                      <span className="admin-product-list-detail-label">Type:</span>
                      <span className="admin-product-list-detail-value">
                        {item.isStandalone ? "Promotion item" : "Linked to product"}
                      </span>
                    </div>
                    <div className="admin-product-list-detail">
                      <span className="admin-product-list-detail-label">Status:</span>
                      <span className="admin-product-list-detail-value">
                        <span className={`admin-promo-status ${item.isActive ? "active" : "inactive"}`}>
                          {item.isActive ? "Active" : "Inactive"}
                        </span>
                      </span>
                    </div>
                  </div>
                </div>

                <div className="admin-product-list-actions-card">
                  <button
                    className="admin-product-list-btn-edit"
                    onClick={() => navigate(`/admin/promotions/edit/${item.id}`)}
                  >
                    <i className="far fa-edit"></i> Edit Promotion
                  </button>
                  <button
                    className="admin-product-list-btn-delete"
                    onClick={() => handleDelete(item.id, item.productName)}
                  >
                    <i className="fas fa-trash-alt"></i> Remove
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>

    </div>
  );
}

export default AdminPromotion;
